import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, ExternalLink, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageLightbox = ({ 
  images, 
  currentIndex, 
  isOpen, 
  onClose, 
  onNavigate,
  token,
  onDownload 
}) => {
  const [loading, setLoading] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [imageError, setImageError] = useState(false);

  const currentImage = images[currentIndex];

  // Reset states when image changes
  useEffect(() => {
    setLoading(true);
    setZoomed(false);
    setImageError(false);
  }, [currentIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        if (currentIndex > 0) onNavigate(currentIndex - 1);
        break;
      case 'ArrowRight':
        if (currentIndex < images.length - 1) onNavigate(currentIndex + 1);
        break;
      default:
        break;
    }
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !currentImage) return null;

  const fullImageUrl = `${API}/images/${currentImage.id}/preview?t=${token}`;

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(currentImage.id);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Top Info Bar */}
      <div className="absolute top-4 left-4 right-16 z-50 flex items-center justify-between">
        <div className="text-white">
          <h3 className="font-medium truncate max-w-md">{currentImage.original_filename}</h3>
          <p className="text-sm text-white/70">
            {currentImage.sender_name} • {currentImage.upload_date}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white/70 text-sm">
            {currentIndex + 1} / {images.length}
          </span>
        </div>
      </div>

      {/* Navigation Buttons */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex - 1);
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>
      )}

      {currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(currentIndex + 1);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Image Container */}
      <div 
        className={`relative max-w-[90vw] max-h-[80vh] flex items-center justify-center ${zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
        onClick={(e) => {
          e.stopPropagation();
          setZoomed(!zoomed);
        }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {imageError ? (
          <div className="text-white text-center p-8">
            <p className="mb-2">Bild konnte nicht geladen werden</p>
            <Button variant="outline" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Trotzdem herunterladen
            </Button>
          </div>
        ) : (
          <img
            src={fullImageUrl}
            alt={currentImage.original_filename}
            className={`
              transition-all duration-300
              ${loading ? 'opacity-0' : 'opacity-100'}
              ${zoomed ? 'max-w-none max-h-none scale-150' : 'max-w-[90vw] max-h-[80vh] object-contain'}
            `}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setImageError(true);
            }}
            data-testid="lightbox-image"
          />
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setZoomed(!zoomed);
          }}
          className="p-2 rounded-full hover:bg-white/20 transition-colors"
          title={zoomed ? 'Verkleinern' : 'Vergrößern'}
          data-testid="lightbox-zoom"
        >
          {zoomed ? (
            <ZoomOut className="w-5 h-5 text-white" />
          ) : (
            <ZoomIn className="w-5 h-5 text-white" />
          )}
        </button>
        
        <div className="w-px h-6 bg-white/30" />
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDownload();
          }}
          className="p-2 rounded-full hover:bg-white/20 transition-colors"
          title="Herunterladen"
          data-testid="lightbox-download"
        >
          <Download className="w-5 h-5 text-white" />
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(`/admin/media/${currentImage.id}`, '_blank');
          }}
          className="p-2 rounded-full hover:bg-white/20 transition-colors"
          title="Details öffnen"
          data-testid="lightbox-details"
        >
          <ExternalLink className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Image Description (if available) */}
      {currentImage.description && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 max-w-lg text-center">
          <p className="text-white/80 text-sm bg-black/50 backdrop-blur-sm rounded-lg px-4 py-2">
            {currentImage.description}
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
